import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import {
  normaliseLine,
  normaliseServerLines,
  mergeByIdOrClientKey,
  reindexPositions,
} from "../utils/ingredientLines";
import { useSidebar } from "./SidebarContext";

const IngredientLinesContext = createContext();

export function IngredientLinesProvider({ children, recipeId }) {
  // Canonical per-recipe state
  const [ingredientLines, setIngredientLines] = useState([]); // IngredientLineRecord[]
  const [selectedLineId, setSelectedLineId] = useState(null); // number | string | null
  const { setSidebarTabCount } = useSidebar();

  const DEFAULT_CONFIDENCE_THRESHOLD = 0.7;

  const lineNeedsReview = useCallback((line, threshold = DEFAULT_CONFIDENCE_THRESHOLD) => {
    if (!line) return false;
    const warningsCount = Array.isArray(line?.warnings) ? line.warnings.length : 0;
    const conf = typeof line?.confidence_score === "number" ? line.confidence_score : null;

    const lowConfidence = conf != null && conf < threshold;
    const unlinked = !line?.ingredient_id;
    const parseError = line?.parse_status === "error";

    return parseError || warningsCount > 0 || lowConfidence || unlinked;
  }, []);

  const needsReviewCount = useMemo(() => {
    return (ingredientLines || []).filter((l) => lineNeedsReview(l)).length;
  }, [ingredientLines, lineNeedsReview]);

  useEffect(() => {
    if (typeof setSidebarTabCount === "function") {
      setSidebarTabCount("ingredients", needsReviewCount);
    }
  }, [needsReviewCount, setSidebarTabCount]);

  // For brand new lines before they have a server id
  const addBlankLine = useCallback(() => {
    setIngredientLines((prev) => {
      const next = [
        ...prev,
        normaliseLine({
          id: null,
          client_key: `tmp_${crypto.randomUUID?.() || Date.now()}`,
          raw_text: "",
          source: "rit_api",
          parse_status: "pending",
        }),
      ];
      return reindexPositions(next);
    });
  }, []);

  const updateLineRawText = useCallback((lineKey, rawText) => {
    setIngredientLines((prev) => {
      const next = prev.map((l) => {
        const key = l.id ?? l.client_key;
        if (key !== lineKey) return l;

        // When user edits text locally, we don’t magically parse here.
        // Parse happens on Save via interpret.
        return {
          ...l,
          raw_text: rawText,
          parse_status: "pending",
          // keep source as-is; if they were manual, it stays manual until “revert to auto” exists
        };
      });
      return next;
    });
  }, []);

  const removeLine = useCallback((lineKey) => {
    setIngredientLines((prev) => {
      const next = prev.filter((l) => (l.id ?? l.client_key) !== lineKey);
      return reindexPositions(next);
    });
  }, []);

  const selectLine = useCallback((lineKey) => {
    setSelectedLineId(lineKey);
  }, []);

  // Authoritative server update after Save (interpret) or refresh
  const applyServerLines = useCallback((serverLines) => {
    const incoming = normaliseServerLines(serverLines);

    setIngredientLines((prev) => {
      // Merge by stable identity:
      // - prefer server `id` when present
      // - fall back to client_key for unsaved lines
      const merged = mergeByIdOrClientKey(prev, incoming);
      return reindexPositions(merged);
    });

    // If selected line was a tmp key and now has a server id, try to preserve selection
    setSelectedLineId((prevSelected) => {
      if (prevSelected == null) return prevSelected;

      // If the selection is a tmp key and now the server returned a real id,
      // map it via client_key.
      const mapped = incoming.find((l) => l.client_key && l.client_key === prevSelected);
      if (mapped) return mapped.id ?? mapped.client_key;

      // Otherwise keep as-is
      return prevSelected;
    });
  }, []);

  // Replace all lines with server lines (for when server sends complete set)
  const replaceServerLines = useCallback((serverLines) => {
    const incoming = normaliseServerLines(serverLines);
    setIngredientLines(reindexPositions(incoming));
  }, []);

  const value = useMemo(
    () => ({
      ingredientLines,
      setIngredientLines, // keep this exposed for now (handy during refactor)
      selectedLineId,
      setSelectedLineId,

      // actions
      addBlankLine,
      updateLineRawText,
      removeLine,
      selectLine,
      applyServerLines,
      replaceServerLines,
    }),
    [
      ingredientLines,
      selectedLineId,
      addBlankLine,
      updateLineRawText,
      removeLine,
      selectLine,
      applyServerLines,
      replaceServerLines,
    ]
  );

  return (
    <IngredientLinesContext.Provider value={value}>
      {children}
    </IngredientLinesContext.Provider>
  );
}

export function useIngredientLines() {
  const ctx = useContext(IngredientLinesContext);
  if (!ctx) throw new Error("useIngredientLines must be used within an IngredientLinesProvider");
  return ctx;
}